/**
 * Infinite showroom (sections/showroom.liquid). Ported from the Next.js Showroom.tsx.
 * - The crowd drifts sideways, follows the pointer, and can be dragged or swiped; it loops forever.
 * - Scrolling down flies the camera into the room (the stage is pinned): figures rush at the viewer,
 *   spread, blur and fade, and the room dissolves into the lookbook beneath. Tied to the scroll position,
 *   so scrolling up flies back out. No pin in the theme editor or with reduced motion.
 * - Hover or focus a figure for its name and price; a click or tap opens the set. A figure whose set is not
 *   live is a focusable span (no href) and shows no pill.
 * Figures are read from the DOM (data-x, data-d, data-w, data-h, data-title, data-price).
 */
const GAP = { desktop: 0.25, tablet: 0.42, phone: 0.62 };
const GAP_MIN_H = 0.5;
const layout = (w, h) => (w < 768 ? 'phone' : h > w ? 'tablet' : 'desktop');
const EDGE = 0.32;
const DRIFT = 22;
const FRICTION = 5.5;
const DRAG_SLOP = 6;
const DRIFT_DELAY = 800;
const HORIZON = 0.48;
const LOOK = { desktop: { foot: 1.03, height: 0.9 }, phone: { foot: 0.9, height: 0.82 } };
const depthScale = (d) => 1 - 0.7 * d;
const restZ = (d) => 1 / depthScale(d);
const DOLLY = 1.8;
const STAGGER = 0.3;
const SPREAD = 1.1;
const AIM = 0.3;
const AIM_X = 0.3;
const NEAR = 0.16;
const FADE = 0.3;
const BLUR_Z = 0.55;
const BLUR_MAX = { desktop: 22, phone: 12 };
// A figure that would cover most of the screen's width fades out before it fills it: on tall, narrow screens
// the figures grow (with the height) faster than they spread aside (with the width), so without this one model
// fills the whole phone screen, blurred, before the room clears.
const COVER_IN = 0.5;
const COVER_OUT = 0.85;
const EMPTY_MAX = 0.7;
const pace = (i) => 1 - STAGGER * ((i * 0.37) % 1);
const clamp01 = (n) => Math.min(1, Math.max(0, n));
const smooth = (a, b, n) => {
  const t = clamp01((n - a) / Math.max(0.0001, b - a));
  return t * t * (3 - 2 * t);
};
const slotX = (x, k) => x + 0.5 / k;

class YusheShowroom extends HTMLElement {
  connectedCallback() {
    this.ac?.abort();
    this.cleanup?.();
    const root = this;
    const stage = this.querySelector('.shw__stage');
    const sticky = this.querySelector('.shw__sticky');
    const pill = this.querySelector('.shw__pill');
    if (!stage || !sticky || !pill) return;
    const els = [...this.querySelectorAll('.shw__fig')];
    const figures = els.map((el) => ({
      x: parseFloat(el.dataset.x),
      d: parseFloat(el.dataset.d),
      w: parseFloat(el.dataset.w),
      h: parseFloat(el.dataset.h),
      title: el.dataset.title,
      price: el.dataset.price,
      link: el.hasAttribute('href'),
      image: el.querySelector('img'),
    }));
    if (!figures.length) return;
    const pillTitle = pill.querySelector('[data-title]');
    const pillPrice = pill.querySelector('[data-price]');
    const editor = Boolean(window.Shopify && window.Shopify.designMode);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches || editor;
    const ac = new AbortController();
    const { signal } = ac;
    this.ac = ac;

    let vw = 0;
    let vh = 0;
    let world = 0;
    let k = 1;
    let look = LOOK.desktop;
    let blurMax = BLUR_MAX.desktop;
    let pin = 0;
    // Followed by the billboard, the room must be clear by the time the held band moves on, wherever the crowd
    // stands (on tall, narrow phones a last figure can linger until the walk's end, leaving the room as a haze over
    // the film): the empty point is capped halfway into the band's hold.
    let emptyMax = EMPTY_MAX;
    let scrollDirty = true;
    let progress = 0;
    let emptyDirty = true;
    const measure = () => {
      const oldWorld = world;
      const oldWidth = vw;
      vw = stage.clientWidth;
      vh = stage.clientHeight;
      const lay = layout(vw, vh);
      const phone = lay !== 'desktop';
      k = (lay === 'desktop' ? Math.max(GAP.desktop, (GAP_MIN_H * vh) / vw) : GAP[lay]) * figures.length;
      world = vw * k;
      look = phone ? LOOK.phone : LOOK.desktop;
      blurMax = phone ? BLUR_MAX.phone : BLUR_MAX.desktop;
      pin = Math.max(0, root.offsetHeight - sticky.offsetHeight);
      const next = root.closest('.shopify-section')?.nextElementSibling;
      const band = next?.querySelector(':scope > .bb');
      emptyMax = EMPTY_MAX;
      if (band && pin > 0) {
        const at = next.getBoundingClientRect().top - root.getBoundingClientRect().top;
        const hold = next.offsetHeight - band.offsetHeight;
        emptyMax = Math.min(EMPTY_MAX, Math.max(0.2, (at + hold / 2) / pin));
      }
      // Keep the same place in the loop through rotation / resizing.
      if (oldWorld > 0 && oldWorld !== world) {
        const ratio = world / oldWorld;
        base *= ratio;
        cam *= ratio;
        if (focusTarget !== null) focusTarget *= ratio;
        lookX *= vw / oldWidth;
        lookTarget *= vw / oldWidth;
        aim = null;
        vel = 0;
      }
      scrollDirty = true;
      emptyDirty = true;
    };

    let base = 0;
    let lookX = 0;
    let lookTarget = 0;
    let cam = 0;
    let vel = 0;
    let driftVelocity = 0;
    let resumeDriftAt = 0;
    let hold = false;
    let focusTarget = null;
    let walk = 0;
    let flown = false;
    let clear = '';
    root.toggleAttribute('data-veiled', !reduce);
    let aim = null;
    const aimFrom = (b) => {
      const gap = world / figures.length;
      return (Math.round(b / gap) + AIM_X) * gap - b;
    };
    let emptyAt = emptyMax;
    let emptyBase = NaN;
    let emptyLook = NaN;
    let emptyAim = null;

    const sx = new Float32Array(figures.length);
    const sy = new Float32Array(figures.length);
    const sc = new Float32Array(figures.length);
    const op = new Float32Array(figures.length).fill(1);

    const at = (i, w, c) => {
      const f = figures[i];
      const m = vw * EDGE;
      const z0 = restZ(f.d);
      const z = Math.max(NEAR / 2, z0 - DOLLY * pace(i) * w);
      const x0 = ((((slotX(f.x, k) * world - c + m) % world) + world) % world) - m;
      return { z, x: vw / 2 + (x0 - vw / 2) * (z0 / z) ** SPREAD };
    };
    const fadeOf = (z) => clamp01((z - NEAR) / FADE);
    const alphaOf = (i, z) => {
      const f = figures[i];
      const cover = (vh * look.height * (f.w / f.h)) / z / vw;
      // On a narrow phone a front figure can already cover half the width at rest: it fades only once it grows
      // past that, so the room starts with every figure whole.
      const from = Math.max(COVER_IN, (cover * z) / restZ(f.d));
      return fadeOf(z) * (1 - smooth(from, from + COVER_OUT - COVER_IN, cover));
    };
    const findEmpty = () => {
      const a = aim ?? aimFrom(base);
      const h0 = vh * look.height;
      for (let w = 0.2; w < emptyMax; w += 0.02) {
        const c = base + lookX * (1 - smooth(0, AIM, w)) + a * smooth(0, AIM, w);
        let seen = false;
        for (let i = 0; i < figures.length && !seen; i++) {
          const { z, x } = at(i, w, c);
          const half = ((h0 * figures[i].w) / figures[i].h / z) * 0.3;
          seen = alphaOf(i, z) > 0.04 && x + half > 0 && x - half < vw;
        }
        if (!seen) return w;
      }
      return emptyMax;
    };

    const project = () => {
      const h0 = vh * look.height;
      if (walk <= 0.01) {
        emptyAt = emptyMax;
        emptyDirty = true;
      } else if (emptyDirty || base !== emptyBase || lookX !== emptyLook || aim !== emptyAim) {
        emptyAt = findEmpty();
        emptyBase = base;
        emptyLook = lookX;
        emptyAim = aim;
        emptyDirty = false;
      }
      // A narrow room can empty before 0.3. Reversed easing edges used to apply full blur and
      // half opacity after even 1px of vertical scroll, switching on/off as the crowd moved sideways.
      const dof = smooth(Math.min(0.3, emptyAt * 0.6), emptyAt, walk);
      // How far the room's backdrop has thinned over the last quarter of the walk, so the section beneath shows
      // behind the last figures instead of an empty room (--clear, used by sections/billboard.liquid).
      const c = smooth(emptyAt * 0.75, emptyAt, walk).toFixed(3);
      if (c !== clear) root.style.setProperty('--clear', (clear = c));
      for (let i = 0; i < figures.length; i++) {
        const f = figures[i];
        const { z, x } = at(i, walk, cam);
        const s = 1 / z;
        sx[i] = x;
        sy[i] = vh * (HORIZON + (look.foot - HORIZON) * s);
        sc[i] = s;
        op[i] = alphaOf(i, z) * (1 - 0.5 * dof * dof);
        const el = els[i];
        const w0 = h0 * (f.w / f.h);
        el.style.transform = `translate3d(${(sx[i] - w0 / 2).toFixed(1)}px,${(sy[i] - h0).toFixed(1)}px,0) scale(${s.toFixed(4)})`;
        el.style.opacity = op[i] < 1 ? op[i].toFixed(3) : '';
        // Cull offscreen image layers, but keep their links focusable so Tab can bring them into view.
        const half = w0 * s / 2;
        const hidden = op[i] < 0.01 || sx[i] + half < -48 || sx[i] - half > vw + 48;
        el.style.visibility = op[i] < 0.01 ? 'hidden' : '';
        if (f.image) f.image.style.visibility = hidden ? 'hidden' : '';
        const bt = clamp01((BLUR_Z - z) / (BLUR_Z - NEAR));
        const b = hidden ? 0 : blurMax * Math.max(bt * bt, 0.6 * dof);
        el.style.filter = b > 0.2 ? `blur(${(b / s).toFixed(2)}px)` : '';
        const gone = op[i] < 0.5;
        if (gone !== el.hasAttribute('data-gone')) el.toggleAttribute('data-gone', gone);
      }
      if (flown !== walk > emptyAt) {
        flown = walk > emptyAt;
        root.toggleAttribute('data-flown', flown);
        root.toggleAttribute('data-veiled', !flown && !reduce);
      }
    };

    const centreOn = (i) => {
      let t = slotX(figures[i].x, k) * world - vw / 2;
      t += Math.round((base - t) / world) * world;
      if (reduce) base = t;
      focusTarget = reduce ? null : t;
    };

    let active = -1;
    let pillDirty = true;
    let pillWidth = 0;
    let pillHeight = 0;
    const setActive = (i) => {
      stage.toggleAttribute('data-focus', i >= 0);
      if (i === active) return;
      if (active >= 0) els[active].removeAttribute('data-active');
      active = i;
      if (i >= 0) {
        els[i].setAttribute('data-active', '');
        pillTitle.textContent = figures[i].title;
        pillPrice.textContent = figures[i].price;
        pill.toggleAttribute('data-nolink', !figures[i].link);
        pillDirty = true;
      }
      pill.toggleAttribute('data-show', i >= 0 && figures[i].link);
    };
    const placePill = () => {
      if (active < 0) return;
      if (op[active] < 0.5 && document.activeElement !== els[active]) return setActive(-1);
      const h = vh * look.height * sc[active];
      const half = pillWidth / 2;
      const ph = pillHeight;
      const pad = 12;
      const top = 76;
      let x = sx[active];
      let y = sy[active] - h - ph - pad;
      if (y < top) {
        const w = h * (figures[active].w / figures[active].h);
        x += (sx[active] + w * 0.32 + half * 2 < vw - pad ? 1 : -1) * (w * 0.32 + half);
        y = Math.max(top, sy[active] - h * 0.9);
      }
      x = Math.min(Math.max(x, half + pad), vw - half - pad);
      pill.style.transform = `translate3d(${(x - half).toFixed(1)}px,${y.toFixed(1)}px,0)`;
    };

    let dragging = false;
    let pointerId = null;
    let axis = '';
    let startX = 0;
    let startY = 0;
    let suppressClick = false;
    let lastX = 0;
    let lastT = 0;
    stage.addEventListener('pointermove', (e) => {
      if (pointerId === null && e.pointerType === 'mouse' && !reduce) {
        lookTarget = (e.clientX / vw - 0.5) * vw * 0.18;
      }
      if (e.pointerId !== pointerId || axis === 'y') return;
      if (!axis) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (Math.max(Math.abs(dx), Math.abs(dy)) < DRAG_SLOP) return;
        axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') return;
        dragging = true;
        suppressClick = true;
        stage.setPointerCapture(e.pointerId);
        stage.setAttribute('data-dragging', '');
      }
      const dx = e.clientX - lastX;
      const now = e.timeStamp;
      const elapsed = Math.max(1, now - lastT);
      base -= dx;
      const speed = Math.max(-vw * 3, Math.min(vw * 3, -dx * 1000 / elapsed));
      vel += (speed - vel) * (1 - Math.exp(-elapsed / 45));
      lastX = e.clientX;
      lastT = now;
    }, { signal });
    stage.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || !e.isPrimary || pointerId !== null) return;
      pointerId = e.pointerId;
      axis = '';
      dragging = false;
      suppressClick = false;
      startX = lastX = e.clientX;
      startY = e.clientY;
      lastT = e.timeStamp;
      vel = 0;
      driftVelocity = 0;
      focusTarget = null;
      hold = false;
      lookTarget = lookX;
      resumeDriftAt = performance.now() + DRIFT_DELAY;
      setActive(-1);
    }, { signal });
    const onUp = (e) => {
      if (e.pointerId !== pointerId) return;
      // A vertical scroll, cancelled touch, or a finger held still must never launch the room sideways.
      if (e.type !== 'pointerup' || !dragging || reduce || e.timeStamp - lastT > 80) vel = 0;
      else vel *= Math.exp(-Math.max(0, e.timeStamp - lastT) / 80);
      const id = pointerId;
      pointerId = null;
      dragging = false;
      stage.removeAttribute('data-dragging');
      if (stage.hasPointerCapture(id)) stage.releasePointerCapture(id);
      resumeDriftAt = performance.now() + DRIFT_DELAY;
    };
    window.addEventListener('pointerup', onUp, { signal });
    window.addEventListener('pointercancel', onUp, { signal });
    stage.addEventListener('lostpointercapture', (e) => {
      // Touch starts with implicit capture on the figure. Its bubbling loss when we capture the
      // stage is a transfer, not a cancelled gesture.
      if (e.target === stage) onUp(e);
    }, { signal });
    stage.addEventListener('pointerleave', () => {
      if (pointerId !== null) return;
      lookTarget = 0;
      if (stage.querySelector(':focus-visible')) return;
      setActive(-1);
      hold = false;
    }, { signal });
    stage.addEventListener('wheel', (e) => {
      const delta = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX;
      if (!e.ctrlKey && (e.shiftKey || Math.abs(delta) > Math.abs(e.deltaY))) {
        e.preventDefault();
        base += delta * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? vw : 1);
        vel = 0;
        driftVelocity = 0;
        focusTarget = null;
        hold = false;
        resumeDriftAt = performance.now() + DRIFT_DELAY;
        setActive(-1);
      }
    }, { passive: false, signal });
    stage.addEventListener('click', (e) => {
      if (suppressClick && e.detail !== 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        suppressClick = false;
      }
    }, { capture: true, signal });
    stage.addEventListener('pointerover', (e) => {
      if (e.pointerType !== 'mouse' || pointerId !== null || Math.abs(vel) > 20) return;
      const a = e.target.closest('.shw__fig');
      setActive(a ? els.indexOf(a) : -1);
      hold = !!a;
    }, { signal });
    stage.addEventListener('focusin', (e) => {
      const a = e.target.closest('.shw__fig');
      // Pointer focus is part of a tap/drag, not a request to recenter the carousel.
      if (!a || pointerId !== null || !a.matches(':focus-visible')) return;
      const i = els.indexOf(a);
      // Browser focus scrolling can jump down the pin before the next animation frame. Correct it
      // immediately using the actual position, not the previous frame's eased walk value.
      const rootTop = root.getBoundingClientRect().top;
      if (rootTop < -1 || op[i] < 0.5) {
        window.scrollTo({ top: window.scrollY + rootTop, behavior: 'instant' });
        walk = progress = 0;
        aim = null;
        scrollDirty = true;
      }
      stage.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      stage.parentElement?.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      setActive(i);
      hold = true;
      vel = 0;
      driftVelocity = 0;
      centreOn(i);
    }, { signal });
    stage.addEventListener('focusout', (e) => {
      if (!stage.contains(e.relatedTarget)) {
        setActive(-1);
        hold = false;
      }
    }, { signal });

    let raf = 0;
    let prev = performance.now();
    window.addEventListener('scroll', () => { scrollDirty = true; }, { passive: true, signal });
    window.addEventListener('resize', () => { pillDirty = true; }, { passive: true, signal });
    const frame = (now) => {
      const dt = Math.min(64, now - prev) / 1000;
      prev = now;
      const damp = (rate) => 1 - Math.exp(-dt * rate);
      // Read layout before writing transforms. A stationary page needs no per-frame layout queries.
      if (scrollDirty) {
        progress = pin > 0 && !reduce ? clamp01(-root.getBoundingClientRect().top / pin) : 0;
        scrollDirty = false;
      }
      if (pillDirty && active >= 0) {
        pillWidth = pill.offsetWidth;
        pillHeight = pill.offsetHeight;
        pillDirty = false;
      }
      walk += (progress - walk) * damp(9);
      if (Math.abs(progress - walk) < 0.0005) walk = progress;
      if (focusTarget !== null) {
        base += (focusTarget - base) * damp(5.6);
        if (Math.abs(focusTarget - base) < 0.5) focusTarget = null;
      } else if (pointerId === null) {
        // Analytic decay gives the same glide distance at 30, 60 and 120Hz.
        const decay = Math.exp(-FRICTION * dt);
        base += vel * (1 - decay) / FRICTION;
        vel *= decay;
        if (Math.abs(vel) < 2) vel = 0;
        const drift = !hold && !reduce && walk < 0.01 && Math.abs(vel) < 5 && now > resumeDriftAt ? DRIFT : 0;
        const driftDecay = Math.exp(-2.5 * dt);
        base += drift * dt + (driftVelocity - drift) * (1 - driftDecay) / 2.5;
        driftVelocity = drift + (driftVelocity - drift) * driftDecay;
      }
      lookX += (lookTarget - lookX) * damp(3);
      if (walk < 0.002) aim = null;
      else aim ??= aimFrom(base);
      const turn = aim === null ? 0 : smooth(0, AIM, walk);
      // Direct horizontal tracking: focus, parallax and release inertia already have their own easing.
      cam = base + lookX * (1 - turn) + (aim ?? 0) * turn;
      project();
      placePill();
      raf = requestAnimationFrame(frame);
    };
    measure();
    project();
    const ro = new ResizeObserver(measure);
    ro.observe(stage);
    ro.observe(sticky);
    ro.observe(root);
    let inView = false;
    const syncAnimation = () => {
      cancelAnimationFrame(raf);
      if (inView && !document.hidden) {
        prev = performance.now();
        scrollDirty = true;
        raf = requestAnimationFrame(frame);
      }
    };
    const io = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      syncAnimation();
    });
    document.addEventListener('visibilitychange', syncAnimation, { signal });
    io.observe(root);
    this.cleanup = () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      ro.disconnect();
    };
  }

  disconnectedCallback() {
    if (this.ac) this.ac.abort();
    if (this.cleanup) this.cleanup();
  }
}

if (!customElements.get('yushe-showroom')) customElements.define('yushe-showroom', YusheShowroom);
